import { Button, cn, Item } from "@tellescope/design-system";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";
import { CalendarClock, RotateCcw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { BlockProps } from "../../../../types";
import { useJourneysBlock } from "../../../../hooks";
import { BlockHeader } from "../../BlockHeader";
import { BlockContent } from "../../BlockContent";
import { BlockEmptyState } from "../../BlockEmptyState";
import { JourneysFilterPopover } from "./JourneysFilterPopover";
import { AddToJourneyPopover } from "./AddToJourneyPopover";

export const JourneysBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  ...rest
}: BlockProps) => {
  const { t } = useTranslation("contacts");
  const { format } = useDateFnsFormattersWithLocale();

  const {
    parsedInfo,
    rows,
    scheduledRows,
    hasFilter,
    addOptions,
    scheduleOptions,
    searchQuery,
    setSearchQuery,
    isSearching,
    addJourney,
    removeJourney,
    restartJourney,
    scheduleJourney,
    unscheduleJourney,
    filterBlock,
    filterFieldOptions,
    journeyNameOptions,
    onFilterChange,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canEditBlock,
    canCreateBlock,
    canDeleteBlock,
    canEditEnduser,
  } = useJourneysBlock({ ...rest, onMaximize, alwaysShowButtons, className });

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          <>
            {canEditEnduser && (
              <AddToJourneyPopover
                options={addOptions}
                schedulableOptions={scheduleOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isSearching={isSearching}
                onAdd={addJourney}
                onSchedule={scheduleJourney}
              />
            )}
            {canEditBlock && (
              <JourneysFilterPopover
                filterBlock={filterBlock}
                fieldOptions={filterFieldOptions}
                journeyNameOptions={journeyNameOptions}
                onFilterChange={onFilterChange}
              />
            )}
          </>
        }
      />
      <BlockContent
        editMode={editMode}
        setEditMode={canEditEnduser ? setEditMode : undefined}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col items-start text-left", contentClassName)}
      >
        {rows.length === 0 && scheduledRows.length === 0 ? (
          <BlockEmptyState className="basis-full">
            {hasFilter ? t("No matching journeys") : t("No journeys")}
          </BlockEmptyState>
        ) : (
          <Item.Group className="w-full">
            {rows.map(({ journeyId, title, state, isArchived }) => (
              <Item key={journeyId} size="sm" className="w-full gap-2 px-2 py-1.5">
                <Item.Content className="flex-row items-baseline gap-2 overflow-hidden">
                  <Item.Title className="shrink-0 truncate">
                    {isArchived ? t("[ARCHIVED] {{title}}", { title }) : title}
                  </Item.Title>
                  <Item.Description
                    className={cn(
                      "truncate text-left text-xs [text-wrap:nowrap]",
                      !state && "italic text-muted-foreground/70"
                    )}
                  >
                    {state || t("No status")}
                  </Item.Description>
                </Item.Content>
                {canEditEnduser && (
                  <Item.Actions className="shrink-0 gap-0">
                    <Button
                      iconOnly
                      size="xs"
                      variant="ghost"
                      color="muted"
                      hoverLabel={t("Restart journey")}
                      onClick={() => restartJourney(journeyId)}
                    >
                      <RotateCcw />
                    </Button>
                    <Button
                      iconOnly
                      size="xs"
                      variant="ghost"
                      color="muted"
                      hoverLabel={t("Remove from journey")}
                      onClick={() => removeJourney(journeyId)}
                    >
                      <X />
                    </Button>
                  </Item.Actions>
                )}
              </Item>
            ))}

            {scheduledRows.map(({ journeyId, title, addAt }) => (
              <Item key={`scheduled-${journeyId}`} size="sm" variant="muted" className="w-full gap-2 px-2 py-1.5">
                <Item.Media className="shrink-0">
                  <CalendarClock size={14} />
                </Item.Media>
                <Item.Content className="flex-row items-baseline gap-2 overflow-hidden">
                  <Item.Title className="shrink-0 truncate">{title}</Item.Title>
                  <Item.Description className="truncate text-left text-xs [text-wrap:nowrap]">
                    {t("Joins {{date}}", { date: format(addAt, "PPp") })}
                  </Item.Description>
                </Item.Content>
                {canEditEnduser && (
                  <Item.Actions className="shrink-0 gap-0">
                    <Button
                      iconOnly
                      size="xs"
                      variant="ghost"
                      color="muted"
                      hoverLabel={t("Cancel scheduled journey")}
                      onClick={() => unscheduleJourney(journeyId)}
                    >
                      <X />
                    </Button>
                  </Item.Actions>
                )}
              </Item>
            ))}
          </Item.Group>
        )}
      </BlockContent>
    </div>
  );
};
